/**
 * Convex Media Service
 *
 * Centralized service for uploading and managing media files in Convex storage.
 * Handles file deduplication, upload management, and URL resolution.
 *
 * IMPORTANT: This service requires an authenticated Convex client.
 * Use the client from useConvex() hook to ensure authentication.
 */

import { ConvexReactClient } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { Id } from "../../../../convex/_generated/dataModel";
import { computeFileHash } from "../utils/general/file-hash";
import { withTimeout, TIMEOUT_DEFAULTS } from "../utils/general/timeout";

export interface ConvexUploadResult {
  storageId: string;
  url: string;
  fileId: string;
  deduplicated: boolean; // True if existing file was reused
}

export interface ConvexMediaFile {
  _id: string;
  userId: string;
  storageId: string;
  fileName: string;
  fileType: "video" | "image" | "audio";
  fileHash: string;
  fileSize: number;
  duration?: number;
  thumbnail?: string;
  mimeType: string;
  lastModified: number;
  createdAt: number;
  updatedAt: number;
}

/**
 * Service for managing media files in Convex storage
 *
 * NOTE: All methods require an authenticated Convex client as first parameter
 */
export class ConvexMediaService {

  /**
   * Check if a file already exists in Convex by hash
   *
   * @param client - Authenticated Convex client
   * @param fileHash - SHA-256 hash of the file
   * @returns Object indicating if file exists and its storage ID
   */
  static async checkFileExists(
    client: ConvexReactClient,
    fileHash: string
  ): Promise<{ exists: boolean; storageId?: string; fileId?: string }> {
    // DON'T wrap in try-catch - let errors propagate so they can be caught and handled properly
    console.log('[ConvexMedia] Checking file hash:', fileHash.substring(0, 16) + '...');

    const result = await withTimeout(
      client.query(api.userMedia.checkFileByHash, {
        fileHash,
      }),
      TIMEOUT_DEFAULTS.QUERY,
      'Timed out checking file existence'
    );

    console.log('[ConvexMedia] Check result:', result);

    if (result.exists) {
      return {
        exists: true,
        storageId: result.storageId,
        fileId: result.fileId,
      };
    }

    return { exists: false };
  }

  /**
   * Upload a file to Convex with deduplication
   *
   * @param client - Authenticated Convex client
   * @param file - File to upload
   * @param fileHash - SHA-256 hash of the file
   * @param thumbnail - Base64 thumbnail data URL
   * @param duration - Duration in seconds (for video/audio)
   * @param onProgress - Optional progress callback (0-100)
   * @returns Upload result with storage ID and URL
   */
  static async uploadFile(
    client: ConvexReactClient,
    file: File,
    fileHash: string,
    thumbnail: string,
    duration?: number,
    onProgress?: (progress: number) => void
  ): Promise<ConvexUploadResult> {
    try {
      // Step 1: Check if file already exists (deduplication)
      console.log('[ConvexMedia] Step 1: Checking file existence...');
      const existing = await this.checkFileExists(client, fileHash);
      console.log('[ConvexMedia] Step 1 complete:', existing);

      if (existing.exists && existing.storageId && existing.fileId) {
        console.log(`[ConvexMedia] File deduplicated, getting URL...`);

        // Get URL for existing file
        const url = await withTimeout(
          client.query(api.userMedia.getFileUrl, {
            storageId: existing.storageId as Id<"_storage">,
          }),
          TIMEOUT_DEFAULTS.QUERY,
          'Timed out getting URL for deduplicated file'
        );
        console.log('[ConvexMedia] Got deduplicated URL:', url);

        if (!url) {
          throw new Error("Failed to get URL for existing file");
        }

        return {
          storageId: existing.storageId,
          url,
          fileId: existing.fileId,
          deduplicated: true,
        };
      }

      // Step 2: Generate upload URL
      console.log('[ConvexMedia] Step 2: Generating upload URL...');
      if (onProgress) onProgress(10);

      const uploadUrl = await withTimeout(
        client.mutation(api.userMedia.generateUploadUrl),
        TIMEOUT_DEFAULTS.MUTATION,
        'Timed out generating upload URL'
      );
      console.log('[ConvexMedia] Step 2 complete, uploadUrl:', uploadUrl?.substring(0, 50) + '...');

      // Step 3: Upload file to storage
      console.log('[ConvexMedia] Step 3: Uploading file to storage...');
      if (onProgress) onProgress(30);

      const uploadResponse = await withTimeout(
        fetch(uploadUrl, {
          method: "POST",
          headers: { "Content-Type": file.type },
          body: file,
        }),
        TIMEOUT_DEFAULTS.UPLOAD,
        'Timed out uploading file to storage'
      );
      console.log('[ConvexMedia] Step 3 complete, status:', uploadResponse.status);

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error('[ConvexMedia] Upload response error:', errorText);
        throw new Error(`Failed to upload file to storage: ${uploadResponse.status} ${errorText}`);
      }

      const { storageId } = await uploadResponse.json();
      console.log('[ConvexMedia] Got storageId:', storageId);
      if (onProgress) onProgress(70);

      // Step 4: Save file metadata
      console.log('[ConvexMedia] Step 4: Saving file metadata...');
      const fileType = this.getFileType(file);

      const fileId = await withTimeout(
        client.mutation(api.userMedia.saveUserMediaFile, {
          storageId: storageId as Id<"_storage">,
          fileName: file.name,
          fileType,
          fileHash,
          fileSize: file.size,
          duration,
          thumbnail,
          mimeType: file.type,
          lastModified: file.lastModified,
        }),
        TIMEOUT_DEFAULTS.MUTATION,
        'Timed out saving file metadata'
      );
      console.log('[ConvexMedia] Step 4 complete, fileId:', fileId);

      if (onProgress) onProgress(90);

      // Step 5: Get download URL
      console.log('[ConvexMedia] Step 5: Getting download URL...');
      const url = await withTimeout(
        client.query(api.userMedia.getFileUrl, {
          storageId: storageId as Id<"_storage">,
        }),
        TIMEOUT_DEFAULTS.QUERY,
        'Timed out getting download URL'
      );
      console.log('[ConvexMedia] Step 5 complete, url:', url);

      if (!url) {
        throw new Error("Failed to get URL for uploaded file");
      }

      if (onProgress) onProgress(100);

      console.log(`[ConvexMedia] ✓ File uploaded successfully: ${file.name}`);

      return {
        storageId,
        url,
        fileId,
        deduplicated: false,
      };
    } catch (error) {
      console.error("[ConvexMedia] ✗ Upload failed:", error);
      throw error;
    }
  }

  /**
   * Fetch all user's media files from Convex
   *
   * @param client - Authenticated Convex client
   * @returns Array of user media files
   */
  static async fetchUserFiles(client: ConvexReactClient): Promise<ConvexMediaFile[]> {
    try {
      const files = await client.query(api.userMedia.listUserMediaFiles);
      return files as ConvexMediaFile[];
    } catch (error) {
      console.error("[Convex] Failed to fetch user files:", error);
      return [];
    }
  }

  /**
   * Get download URL for a storage ID
   *
   * @param client - Authenticated Convex client
   * @param storageId - Convex storage ID
   * @returns Download URL or null if not found
   */
  static async getFileUrl(client: ConvexReactClient, storageId: string): Promise<string | null> {
    try {
      const url = await client.query(api.userMedia.getFileUrl, {
        storageId: storageId as Id<"_storage">,
      });
      return url;
    } catch (error) {
      console.error("[Convex] Failed to get file URL:", error);
      return null;
    }
  }

  /**
   * Batch resolve URLs for multiple storage IDs
   *
   * @param client - Authenticated Convex client
   * @param storageIds - Array of Convex storage IDs
   * @returns Map from storageId to URL (or null)
   */
  static async getFileUrlsBatch(
    client: ConvexReactClient,
    storageIds: string[]
  ): Promise<Map<string, string | null>> {
    if (storageIds.length === 0) return new Map();

    try {
      const results = await client.query(api.userMedia.getFileUrlsBatch, {
        storageIds: storageIds as Id<"_storage">[],
      });
      const map = new Map<string, string | null>();
      for (const { storageId, url } of results) {
        map.set(storageId, url);
      }
      return map;
    } catch (error) {
      console.error("[ConvexMedia] Failed to batch resolve storage IDs:", error);
      return new Map();
    }
  }

  /**
   * Batch resolve URLs for multiple file hashes
   *
   * @param client - Authenticated Convex client
   * @param fileHashes - Array of file hashes
   * @returns Map from fileHash to URL (or null)
   */
  static async getUrlsByHashBatch(
    client: ConvexReactClient,
    fileHashes: string[]
  ): Promise<Map<string, string | null>> {
    if (fileHashes.length === 0) return new Map();

    try {
      const results = await client.query(api.userMedia.getUrlsByHashBatch, {
        fileHashes,
      });
      const map = new Map<string, string | null>();
      for (const { fileHash, url } of results) {
        map.set(fileHash, url);
      }
      return map;
    } catch (error) {
      console.error("[ConvexMedia] Failed to batch resolve file hashes:", error);
      return new Map();
    }
  }

  /**
   * Delete a media file from Convex
   *
   * @param client - Authenticated Convex client
   * @param fileId - File ID to delete
   */
  static async deleteFile(client: ConvexReactClient, fileId: string): Promise<void> {
    try {
      await client.mutation(api.userMedia.deleteUserMediaFile, {
        fileId: fileId as Id<"userMediaFiles">,
      });
      console.log(`[Convex] File deleted: ${fileId}`);
    } catch (error) {
      console.error("[Convex] Failed to delete file:", error);
      throw error;
    }
  }

  /**
   * Upload file with automatic hash calculation
   *
   * Convenience method that calculates hash automatically
   *
   * @param client - Authenticated Convex client
   * @param file - File to upload
   * @param thumbnail - Base64 thumbnail data URL
   * @param duration - Duration in seconds (for video/audio)
   * @param onProgress - Optional progress callback
   * @returns Upload result
   */
  static async uploadFileWithHash(
    client: ConvexReactClient,
    file: File,
    thumbnail: string,
    duration?: number,
    onProgress?: (progress: number) => void
  ): Promise<ConvexUploadResult> {
    // Calculate hash (0-10% progress)
    if (onProgress) onProgress(0);
    const fileHash = await computeFileHash(file);
    if (onProgress) onProgress(10);

    // Upload file (10-100% progress)
    const progressOffset = 10;
    const progressScale = 90;

    return this.uploadFile(
      client,
      file,
      fileHash,
      thumbnail,
      duration,
      onProgress
        ? (p) => onProgress(progressOffset + (p * progressScale) / 100)
        : undefined
    );
  }

  /**
   * Get file type from File object
   */
  private static getFileType(file: File): "video" | "image" | "audio" {
    if (file.type.startsWith("video/")) return "video";
    if (file.type.startsWith("image/")) return "image";
    if (file.type.startsWith("audio/")) return "audio";
    throw new Error(`Unsupported file type: ${file.type}`);
  }
}
